import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { PortfolioApi, PortfolioListOrderingEnum } from 'api/apis';
import { InvestorPortfolio } from 'api/models';
import Translate from 'ui/atoms/translate';
import makeLink from 'helper/make-link';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import WithDataRecord from 'hoc/WithDataRecord';
import WideContent from 'core/layout/content/wide-content';
import DashboardPortfolioTable from './dashboard-portfolio-table';
import { convertProductTypeFromApi } from 'core/api/conversions';
import { INVESTOR_ROUTES } from 'apps/investor/pages/routes.config';
import { useDispatch } from 'store/hooks';
import { push } from 'connected-react-router';
import Button from 'ui/atoms/button';
import AddProductModal from './add-product-modal';
import { useApiCall } from 'hooks/use-api-call';

const InvestorPortfolioTable: FunctionComponent<{}> = WithDataRecord<PortfolioApi, InvestorPortfolio>(PortfolioApi)(
  ({ data, ordering, onOrderBy, loading, paginationProps }) => {
    const dispatch = useDispatch();

    const goTo = (id: string) => {
      dispatch(push(makeLink(INVESTOR_ROUTES.product, { id })));
    };

    return (
      <>
        <DashboardPortfolioTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          loading={loading}
          investorPortfolio={(data || []).map((portfolio) => ({
            ...portfolio,
            productType: convertProductTypeFromApi(portfolio.productType),
          }))}
          goToDetails={(productId: string) => goTo(productId)}
          paginationProps={paginationProps}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.portfolioList({
      offset,
      limit,
      ...props,
      ordering: ordering as PortfolioListOrderingEnum,
    });
  },
);

const InvestorPortfolioPage: FunctionComponent<{}> = () => {
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasAvailableProducts, setHasAvailableProducts] = useState(false);
  const { withApi, makeAuthenticatedApi } = useApiCall();
  const api = useMemo(() => makeAuthenticatedApi(PortfolioApi), [makeAuthenticatedApi]);

  useEffect(() => {
    withApi(async () => {
      const res = await api.portfolioAvailableProductsList({});
      setHasAvailableProducts(res.length > 0);
    });
  }, [withApi, api, refreshKey]);

  return (
    <WideContent
      title={<Translate name="dashboardInvestorPortfolio.title" />}
      headerPanel={
        <Button
          variant="primary"
          size="small"
          icon="plus"
          iconColor="inherit"
          disabled={!hasAvailableProducts}
          onClick={() => setShowAddProduct(true)}
        >
          <Translate name="dashboardInvestorPortfolio.addProduct" />
        </Button>
      }
    >
      <InvestorPortfolioTable key={refreshKey} />
      {showAddProduct && (
        <AddProductModal onClose={() => setShowAddProduct(false)} onSuccess={() => setRefreshKey((k) => k + 1)} />
      )}
    </WideContent>
  );
};

export default WithAuthenticatedPage(InvestorPortfolioPage, AppType.INVESTOR);
